import * as React from 'react';

import moment from 'moment';

import {Radio} from 'shells/radio/radio';
import {RadioGroup} from 'shells/radio/radio-group';
import {Select} from 'shells/select/select';
import {TextFieldDebounced} from 'shells/textfield/textfield-debounced';
import {SAVED_LIST_TOUR_NEXT_FILTER_ID} from 'shells/tours/tours';

import {FormDatePicker} from './form-date-picker';

import styles from './tiered-radio-button-form.module.css';

// @ts-expect-error
const selectValues = (dataValue) => {
    const s = dataValue === 1 ? '' : 's';

    return [
        {id: 'minutes', value: `minute${s}`},
        {id: 'hours', value: `hour${s}`},
        {id: 'days', value: `day${s}`},
        {id: 'weeks', value: `week${s}`},
        {id: 'months', value: `month${s}`},
        {id: 'years', value: `year${s}`},
    ];
};

const currentValues = [
    {id: 'day', value: 'day'},
    {id: 'week', value: 'week'},
    {id: 'month', value: 'month'},
    {id: 'quarter', value: 'quarter'},
    {id: 'year', value: 'year'},
];

const toDateValues = [
    {id: 'week', value: 'week-to-date'},
    {id: 'month', value: 'month-to-date'},
    {id: 'quarter', value: 'quarter-to-date'},
    {id: 'year', value: 'year-to-date'},
];

const defaultValues = {
    within: {
        selected: 'days',
        data: '7',
    },
    lessThan: {
        selected: 'minutes',
        data: '30',
    },
    moreThan: {
        selected: 'minutes',
        data: '30',
    },
    current: {
        selected: 'month',
    },
    todate: {
        selected: 'month',
    },
    between: {
        startDate: moment().unix(),
        endDate: moment().add(7, 'days').unix(),
    },
    next: {
        selected: 'weeks',
        data: '4',
    },
    never: {
        data: {},
    },
    any: {
        data: {},
    },
} as const;

type Props = {
    value?: any;
    name: string;
    onChange: (arg1: any) => any;
    isFutureFilterable: boolean;
    isBetweenFilterable: boolean;
    isOlderThanFilterable: boolean;
    isLessThanFilterable: boolean;
    isMoreThanFilterable: boolean;
    isCurrentFilterable: boolean;
    isToDateFilterable: boolean;
    nullOptionText?: string;
    anyOptionText?: string;
};
type State = {
    selected?: string;
    data: any;
};
export class FormDateTime extends React.Component<Props, State> {
    static defaultProps = {
        isBetweenFilterable: true,
        isOlderThanFilterable: true,
        isLessThanFilterable: false,
        isMoreThanFilterable: false,
        isCurrentFilterable: false,
        isFutureFilterable: false,
        isToDateFilterable: false,
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            selected: props.value && props.value.selected ? props.value.selected : undefined,
            data: props.value && props.value.data ? props.value.data : {},
        };
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const selectionChanged = prevState.selected !== this.state.selected;
        const dataChanged =
            prevState.data.data !== this.state.data.data &&
            this.state.data.data &&
            this.state.data.data.length;
        const startOrEndDateChanged =
            prevState.data.startDate !== this.state.data.startDate ||
            prevState.data.endDate !== this.state.data.endDate;

        if (selectionChanged || dataChanged || startOrEndDateChanged) {
            this.handleChange();
        }
    }

    render() {
        return (
            <RadioGroup
                selectedValue={this.state.selected}
                name={this.props.name}
                onChange={this.handleRadioSelect}
            >
                {this.props.isCurrentFilterable ? <Radio value='current'>Current</Radio> : null}
                {this.props.isCurrentFilterable && this.state.selected === 'current' ? (
                    <div className={styles['textfield-container']}>
                        <Select
                            labelKey='value'
                            value={currentValues.find((val) => val.id === this.state.data.selected)}
                            onChange={this.handlePeriodChange}
                            selectValues={currentValues}
                        />
                    </div>
                ) : null}
                {this.props.isToDateFilterable ? <Radio value='todate'>To-date</Radio> : null}
                {this.props.isToDateFilterable && this.state.selected === 'todate' ? (
                    <div className={styles['textfield-container']}>
                        <Select
                            labelKey='value'
                            value={toDateValues.find((val) => val.id === this.state.data.selected)}
                            onChange={this.handlePeriodChange}
                            selectValues={toDateValues}
                        />
                    </div>
                ) : null}
                <Radio value='within'>
                    {/* Our overdueTime is a date to be referened from a different frame of mind,
                    i.e. "less than 3 days overdue" rather than "overdue within the last 3 days"
                    so this allows us to use isLessThanFilterable to give the filter UI better
                    messaging */}
                    {this.props.isLessThanFilterable ? 'Less than' : 'Within the last'}
                </Radio>
                {this.state.selected === 'within' ? (
                    <div className={styles['textfield-container--linked-select']}>
                        <TextFieldDebounced
                            autoFocus={true}
                            type='number'
                            // @ts-expect-error
                            min={1}
                            // @ts-expect-error
                            max={999}
                            delay={500}
                            value={this.state.data.data}
                            onChange={this.handleTextChange}
                        />
                        <Select
                            labelKey='value'
                            value={selectValues(this.state.data.data).find(
                                (val) => val.value === this.state.data.selected
                            )}
                            onChange={this.handlePeriodChange}
                            selectValues={selectValues(this.state.data.data)}
                        />
                    </div>
                ) : null}
                {this.props.isFutureFilterable ? (
                    // @ts-expect-error
                    <Radio value='next' id={SAVED_LIST_TOUR_NEXT_FILTER_ID}>
                        In the next
                    </Radio>
                ) : null}
                {this.props.isFutureFilterable && this.state.selected === 'next' ? (
                    <div className={styles['textfield-container--linked-select']}>
                        <TextFieldDebounced
                            autoFocus={true}
                            type='number'
                            // @ts-expect-error
                            min={1}
                            // @ts-expect-error
                            max={999}
                            delay={500}
                            value={this.state.data.data}
                            onChange={this.handleTextChange}
                        />
                        <Select
                            labelKey='value'
                            value={selectValues(this.state.data.data).find(
                                (val) => val.value === this.state.data.selected
                            )}
                            onChange={this.handlePeriodChange}
                            selectValues={selectValues(this.state.data.data)}
                        />
                    </div>
                ) : null}
                {this.props.isOlderThanFilterable ? (
                    <Radio value='lessThan'>Older than</Radio>
                ) : null}
                {this.props.isOlderThanFilterable && this.state.selected === 'lessThan' ? (
                    <div className={styles['textfield-container--linked-select']}>
                        <TextFieldDebounced
                            autoFocus={true}
                            type='number'
                            // @ts-expect-error
                            min={1}
                            // @ts-expect-error
                            max={999}
                            delay={500}
                            value={this.state.data.data}
                            onChange={this.handleTextChange}
                        />
                        <Select
                            labelKey='value'
                            value={selectValues(this.state.data.data).find(
                                (val) => val.id === this.state.data.selected
                            )}
                            onChange={this.handlePeriodChange}
                            selectValues={selectValues(this.state.data.data)}
                        />
                    </div>
                ) : null}
                {/* This is another frame of mind change up that was introduced
                for overdueTime.  A bit confusing, but isMoreThanFilterable
                adds "More than 3 days overdue" to the UI so the user can
                better think about dates using this type, but this actually
                maps directly to our lessThan value type that gets sent through
                to solr. More than 3 days overdue === any time less than 3 days ago */}
                {this.props.isMoreThanFilterable ? <Radio value='lessThan'>More than</Radio> : null}
                {this.props.isMoreThanFilterable && this.state.selected === 'lessThan' ? (
                    <div className={styles['textfield-container']}>
                        <TextFieldDebounced
                            autoFocus={true}
                            type='number'
                            // @ts-expect-error
                            min={1}
                            // @ts-expect-error
                            max={999}
                            delay={500}
                            value={this.state.data.data}
                            onChange={this.handleTextChange}
                        />
                        <Select
                            labelKey='value'
                            value={selectValues(this.state.data.data).find(
                                (val) => val.id === this.state.data.selected
                            )}
                            onChange={this.handlePeriodChange}
                            selectValues={selectValues(this.state.data.data)}
                        />
                    </div>
                ) : null}
                {this.props.isBetweenFilterable ? <Radio value='between'>Between</Radio> : null}
                {this.props.isBetweenFilterable && this.state.selected === 'between' ? (
                    <div className={styles['textfield-container--horizontal']}>
                        <FormDatePicker
                            // @ts-expect-error
                            name={`${this.props.name}.between.startDate`}
                            value={this.state.data.startDate}
                            onChange={this.handleStartDateChange}
                            dateFormat='D MMMM YYYY'
                        />
                        <span className={styles['between-divider']}>and</span>
                        <FormDatePicker
                            // @ts-expect-error
                            name={`${this.props.name}.between.endDate`}
                            value={this.state.data.endDate}
                            onChange={this.handleEndDateChange}
                            dateFormat='D MMMM YYYY'
                        />
                    </div>
                ) : null}
                {this.props.nullOptionText ? (
                    <Radio value='never'>{this.props.nullOptionText}</Radio>
                ) : null}
                {this.props.anyOptionText ? (
                    <Radio value='any'>{this.props.anyOptionText}</Radio>
                ) : null}
            </RadioGroup>
        );
    }

    handlePeriodChange = (newPeriod: {id: string}) => {
        this.setState(
            (prevState) => ({
                data: {...prevState.data, selected: newPeriod.id},
            }),
            this.handleChange
        );
    };

    handleRadioSelect = (val: string) => {
        if (val !== this.state.selected) {
            this.setState({
                selected: val,
                // @ts-expect-error
                data: defaultValues[val],
            });
        }
    };

    handleTextChange = (newValue: string) => {
        this.setState((prevState) => {
            return {
                data: {...prevState.data, data: newValue},
            };
        });
    };

    handleChange = () => {
        this.props.onChange({
            [this.props.name]: {
                selected: this.state.selected,
                data: this.state.data,
            },
        });
    };

    handleStartDateChange = (date: number) => {
        this.setState((prevState) => {
            const newData = {
                startDate: date,
                // Prevent start date after end date
                endDate: moment(date).isAfter(moment(prevState.data.endDate))
                    ? date
                    : prevState.data.endDate,
            } as const;

            return {
                data: newData,
            };
        });
    };

    handleEndDateChange = (date: number) => {
        this.setState((prevState) => {
            const newData = {
                // Prevent end date before start date
                startDate: moment(date).isBefore(moment(prevState.data.startDate))
                    ? date
                    : prevState.data.startDate,
                endDate: date,
            } as const;

            return {
                data: newData,
            };
        });
    };
}
